import React from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyWhoMode({ brand, today, Copy }: ModeRendererProps) {
  // WHO gradient: global health spectrum
  const whoGradient = (
    <defs>
      <linearGradient id="whoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#009CDE" />
        <stop offset="50%" stopColor="#4CAF50" />
        <stop offset="100%" stopColor="#FFC107" />
      </linearGradient>
    </defs>
  );

  // Coverage grids: health service access
  const coverageGrids: JSX.Element[] = [];
  for (let row = 0; row < 16; row++) {
    for (let col = 0; col < 12; col++) {
      const x = 80 + col * 55;
      const y = 120 + row * 55;
      const coverage = 0.3 + Math.random() * 0.7;
      const color = coverage < 0.5 ? "#FFC107" : coverage < 0.8 ? "#4CAF50" : "#009CDE";
      coverageGrids.push(
        <circle
          key={`cov-${row}-${col}`}
          cx={x}
          cy={y}
          r={6 + coverage * 12}
          fill={color}
          opacity={0.4 + coverage * 0.3}
        />
      );
    }
  }

  // Cold/warm palettes: disease burden
  const diseaseWaves: JSX.Element[] = [];
  for (let i = 0; i < 14; i++) {
    const y = 120 + i * 65;
    const points = [];
    for (let j = 0; j < 13; j++) {
      const x = 70 + j * 55;
      const burden = Math.sin(j * 0.6) * 35;
      points.push(`${x},${y + burden}`);
    }
    diseaseWaves.push(
      <polyline
        key={`disease-${i}`}
        points={points.join(" ")}
        fill="none"
        stroke="url(#whoGrad)"
        strokeWidth={1.5}
        opacity={0.4}
      />
    );
  }

  // Care networks: health system connections
  const careNetworks: JSX.Element[] = [];
  const hospitalCount = 20;
  for (let i = 0; i < hospitalCount; i++) {
    const angle = (i * Math.PI * 2) / hospitalCount;
    const radius = 180;
    const x = 397 + radius * Math.cos(angle);
    const y = 561.5 + radius * Math.sin(angle);
    careNetworks.push(
      <g key={`care-${i}`}>
        <circle cx={x} cy={y} r={14} fill="url(#whoGrad)" opacity={0.4} />
        {/* Medical cross */}
        <line x1={x} y1={y - 6} x2={x} y2={y + 6} stroke="#FFFFFF" strokeWidth={2} />
        <line x1={x - 6} y1={y} x2={x + 6} y2={y} stroke="#FFFFFF" strokeWidth={2} />
      </g>
    );
    
    // Connect to center
    careNetworks.push(
      <line
        key={`care-link-${i}`}
        x1={x}
        y1={y}
        x2={397}
        y2={561.5}
        stroke="url(#whoGrad)"
        strokeWidth={0.5}
        opacity={0.2}
      />
    );
  }

  // Health equity rings
  const equityRings: JSX.Element[] = [];
  for (let i = 0; i < 18; i++) {
    const radius = 50 + i * 18;
    equityRings.push(
      <circle
        key={`equity-${i}`}
        cx={397}
        cy={561.5}
        r={radius}
        fill="none"
        stroke="url(#whoGrad)"
        strokeWidth={1}
        opacity={0.2}
      />
    );
  }

  // Central WHO core
  const whoCore = (
    <g opacity={0.9}>
      <circle cx={397} cy={561.5} r={80} fill="none" stroke="#4CAF50" strokeWidth={3} />
      <circle cx={397} cy={561.5} r={55} fill="none" stroke="#009CDE" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={35} fill="none" stroke="#FFC107" strokeWidth={2} />
      {/* Rod of Asclepius */}
      <line x1={397} y1={521.5} x2={397} y2={601.5} stroke="#FFFFFF" strokeWidth={3} />
      <path
        d="M 387 531.5 Q 397 541.5 407 531.5 Q 397 551.5 387 541.5 Q 397 561.5 407 551.5"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth={2.5}
      />
    </g>
  );

  return (
    <g>
      {whoGradient}
      {equityRings}
      {diseaseWaves}
      {coverageGrids}
      {careNetworks}
      {whoCore}
      {/* Render Copy object safely (Copy from constants, not a component) */}
      {Copy && typeof Copy === "object" && (
        <g fontFamily="monospace" fontSize={9} fill="#FFFFFF" opacity={0.9}>
          <text x={397} y={1040} textAnchor="middle" fontWeight={700}>
            {Copy.title}
          </text>
          <text x={397} y={1072} textAnchor="middle">
            {/* date/version removed */}
          </text>
          <text x={397} y={1072} textAnchor="middle">
            {Copy.date} {Copy.version ? `— ${Copy.version}` : ""}
          </text>
        </g>
      )}
    </g>
  );
}
